import { VolumeAllocationValidation } from '@ordersnproducts/modules/order-builder/models/volume-allocation-validation.model';
import { OrderBuilderActions, RESET_BUILDER } from '../../../actions/order-builder.actions';
import { RecipeFormValues } from '../../retail-specification/models/form-values';
import { SpecFieldNames } from '../../specification/constants/specification-field-names.enum';
import { SpecificationFormValues } from '../../specification/models/specification-form-values';
import { RmsSpecificationActions, RmsSpecificationActionTypes } from '../actions/rms-specification.actions';
import {
  VolumeAllocationCodes,
  VolumeAllocationErrorCodes,
} from '@ordersnproducts/modules/shared/constants/volume-allocation';

export interface RmsSpecificationFormState {
  forms: RecipeFormValues[];
  updateForm: SpecificationFormValues;
  backSummary: boolean;
  ready: boolean;
  rehydrate: {
    [id: number]: {
      [key: string]: any;
    };
  };
  isModify: boolean;
  hasRawMaterial: boolean;
  volumeAllocationValidation: VolumeAllocationValidation;
}

export const initialState: RmsSpecificationFormState = {
  forms: [],
  updateForm: null,
  ready: false,
  backSummary: false,
  rehydrate: null,
  isModify: false,
  hasRawMaterial: false,
  volumeAllocationValidation: { message: '', invalid: false, code: '', loading: false },
};

let formCounter = -1;

/**
 * Adds an item to the recipe
 */
const makeRecipeItem = (values: { [key: string]: any }): RecipeFormValues => {
  const id = ++formCounter;
  return {
    id: id,
    values: { ...values },
    isReady: false,
  };
};
export const makeRecipeItemsId = (values: { [key: string]: any }): RecipeFormValues => {
  const id = ++formCounter;
  return {
    id: id,
    values: { ...values, id },
    isReady: false,
  };
};
const makeSeqNum = (item: RecipeFormValues, index: number): number => {
  return 10 * (index + 1);
};

const makeUpdateForm = (state: RmsSpecificationFormState, payload: SpecificationFormValues) => {
  const formState = state.forms.find((formItem) => formItem.values.id === payload.id);
  let formUpdate;
  if (formState) {
    if (formState.values.productSelect === payload.values.productSelect && !state.backSummary) {
      formUpdate = state.updateForm;
    } else if (formState.values.productSelect !== payload.values.productSelect && !!payload.values.productSelect) {
      formUpdate = formState;
    } else if (formState.values.productSelect === payload.values.productSelect && state.backSummary) {
      formUpdate = null;
    }
  } else {
    formUpdate = null;
  }

  return formUpdate;
};

export function rmsSpecificationFormReducer(
  state = initialState,
  action: RmsSpecificationActions | OrderBuilderActions,
): RmsSpecificationFormState {
  switch (action.type) {
    case RmsSpecificationActionTypes.ADD_ITEM: {
      const form = action.payload as RecipeFormValues;
      return {
        ...state,
        forms: [...state.forms, form],
      };
    }
    case RmsSpecificationActionTypes.RESET_UPDATE_FORM: {
      return {
        ...state,
        updateForm: null,
        backSummary: true,
      };
    }
    case RmsSpecificationActionTypes.UPDATE_ITEM: {
      return {
        ...state,
        updateForm: makeUpdateForm(state, action.payload),
        forms: state.forms.map((item) => {
          if (item.id === action.payload.id) {
            const quantity = action.payload.values[SpecFieldNames.QUANTITY];
            return {
              ...action.payload,
              values: {
                ...action.payload.values,
                [SpecFieldNames.ORDER_ID]: item.values.orderId,
                [SpecFieldNames.QUANTITY]: quantity && typeof quantity === 'number' && +quantity.toFixed(6),
              },
            };
          } else {
            return item;
          }
        }),
      };
    }
    case RmsSpecificationActionTypes.UPDATE_ITEM_PARTIAL_ITEM: {
      return {
        ...state,
        forms: state.forms.map((item) => {
          if (item.id === action.payload.id) {
            return {
              ...item,
              values: {
                ...item.values,
                ...action.payload.values,
              },
            };
          } else {
            return item;
          }
        }),
      };
    }
    case RmsSpecificationActionTypes.SET_ITEMS_SEQ_NUM: {
      return {
        ...state,
        forms: state.forms.map((item, index) => {
          return { ...item, values: { ...item.values, [SpecFieldNames.ITEM_SEQ_NUM]: makeSeqNum(item, index) } };
        }),
      };
    }
    case RmsSpecificationActionTypes.RESET_ITEMS_SEQ_NUM: {
      return {
        ...state,
        forms: state.forms.map((item, index) => {
          return { ...item, values: { ...item.values, [SpecFieldNames.ITEM_SEQ_NUM]: undefined } };
        }),
      };
    }
    case RmsSpecificationActionTypes.REMOVE_ITEM: {
      return {
        ...state,
        forms: state.forms.filter((i) => i.id !== action.payload),
        updateForm: state.forms.find((item) => item.id === action.payload - 1),
      };
    }
    case RmsSpecificationActionTypes.REMOVE_ALL_ITEMS_EXCEPT: {
      return {
        ...state,
        forms: state.forms.filter((i) => i.id === action.payload),
      };
    }
    case RmsSpecificationActionTypes.SET_FORM_READY: {
      return {
        ...state,
        ready: action.payload,
      };
    }
    case RmsSpecificationActionTypes.SET_INDIVIDUAL_FORM_READY: {
      return {
        ...state,
        forms: state.forms.map((item, index) => {
          if (index === action.payload.formIndex) {
            return { ...item, isReady: action.payload.isReady };
          }
          return { ...item };
        }),
      };
    }
    case RmsSpecificationActionTypes.SET_REHYDRATE: {
      return {
        ...state,
        rehydrate: { ...action.payload },
      };
    }
    case RmsSpecificationActionTypes.REMOVE_REHYDRATE: {
      const _productRehydratation = { ...state.rehydrate[action.payload.id] };
      delete _productRehydratation[action.payload.key];
      return {
        ...state,
        rehydrate: {
          ...state.rehydrate,
          [action.payload.id]: _productRehydratation,
        },
      };
    }
    case RESET_BUILDER:
    case RmsSpecificationActionTypes.RESET: {
      formCounter = -1;
      return {
        ...initialState,
      };
    }
    case RmsSpecificationActionTypes.LOAD_RECIPES: {
      const recipesArray = [];
      action.payload.forEach((item) => {
        recipesArray.push(makeRecipeItem(item));
      });
      return {
        ...state,
        forms: recipesArray,
      };
    }
    case RmsSpecificationActionTypes.LOAD_RECIPES_MODIFY: {
      const recipesArray = [];
      action.payload.forEach((item) => {
        recipesArray.push(makeRecipeItemsId(item));
      });
      return {
        ...state,
        forms: recipesArray,
        isModify: true,
      };
    }

    case RmsSpecificationActionTypes.SET_RAW_MATERIAL: {
      return {
        ...state,
        hasRawMaterial: action.payload,
      };
    }

    case RmsSpecificationActionTypes.GET_VOLUME_MANAGEMENT_SUCCESS: {
      let volumeState = action.payload;
      if (!action.payload.invalid) {
        const invalid = VolumeAllocationErrorCodes.some(
          (errorCode: VolumeAllocationCodes) => errorCode[action.payload.code] !== undefined,
        );
        volumeState = { ...action.payload, invalid: invalid, loading: false };
      }
      return {
        ...state,
        volumeAllocationValidation: volumeState,
      };
    }

    case RmsSpecificationActionTypes.GET_VOLUME_MANAGEMENT_ERROR: {
      return {
        ...state,
        volumeAllocationValidation: {
          message: '',
          invalid: false,
          loading: false,
          code: '',
        },
      };
    }

    case RmsSpecificationActionTypes.LOADING_VOLUME_MANAGEMENT: {
      return {
        ...state,
        volumeAllocationValidation: {
          ...state.volumeAllocationValidation,
          loading: true,
        },
      };
    }

    case RmsSpecificationActionTypes.RESET_VOLUME_MANAGEMENT: {
      return {
        ...state,
        volumeAllocationValidation: {
          message: '',
          invalid: false,
          loading: false,
          code: '',
        },
      };
    }

    default: {
      return state;
    }
  }
}
